import React, { useMemo } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import {
  ExclamationTriangleIcon,
  XMarkIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import ScoreProgress from "./ScoreProgress";
import { getProgrammeTypeDisplayName } from "../utils/accreditationRules";

/**
 * ConfirmationModal Component
 * Modal for submission confirmation with dynamic template score summary
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Close handler
 * @param {function} onConfirm - Confirm handler
 * @param {array} templates - Array of template objects
 * @param {object} templateScores - Scores organized by template ID
 * @param {string} programmeType - Programme type (UG, PG, ODL)
 * @param {boolean} isLoading - Loading state
 */
const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  templates = [],
  templateScores = {},
  programmeType = "UG",
  isLoading = false,
}) => {
  const programmeTypeDisplay = getProgrammeTypeDisplayName(programmeType);

  // Calculate total scores from all templates (using same logic as Dashboard)
  const { totalScore, maxScore, percentage, coreTemplates, supplementaryTemplates } = useMemo(() => {
    let total = 0;
    let max = 0;
    const core = [];
    const supplementary = [];

    templates.forEach((template) => {
      const templateScore = templateScores[template._id];

      // Use metadata from backend if available (same as Dashboard)
      const templateTotal = templateScore?.metadata?.totalScore || 0;
      const templateMax = templateScore?.metadata?.maxPossibleScore || 0;

      const templateInfo = {
        id: template._id,
        title: template.title || template.name || 'Untitled Template',
        score: templateTotal,
        maxScore: templateMax,
        percentage: templateMax > 0 ? (templateTotal / templateMax) * 100 : 0,
        type: template.type || 'core',
      };

      if (template.type === 'core') {
        core.push(templateInfo);
      } else {
        supplementary.push(templateInfo);
      }

      total += templateTotal;
      max += templateMax;
    });

    return {
      totalScore: total,
      maxScore: max,
      percentage: max > 0 ? (total / max) * 100 : 0,
      coreTemplates: core,
      supplementaryTemplates: supplementary,
    };
  }, [templates, templateScores]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                        <ExclamationTriangleIcon className="h-6 w-6 text-white" />
                      </div>
                      <div className="text-left">
                        <Dialog.Title
                          as="h3"
                          className="text-xl font-bold text-white"
                        >
                          Confirm Submission
                        </Dialog.Title>
                        <p className="text-sm text-blue-100 mt-0.5">
                          Review your scores before final submission
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={onClose}
                      disabled={isLoading}
                      className="rounded-lg p-1.5 text-white hover:bg-white hover:bg-opacity-20 transition-colors duration-200 disabled:opacity-50"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Body */}
                <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">
                  {/* Warning Message */}
                  <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-yellow-800">
                          <span className="font-semibold">Important:</span>{" "}
                          Once submitted, these scores will be recorded and
                          cannot be easily modified. Please review carefully.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Total Score Display */}
                  <div className="mb-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5 border-2 border-blue-200">
                    <div className="text-center mb-4">
                      <div className="text-5xl font-bold text-blue-900">
                        {totalScore.toFixed(2)}
                        <span className="text-2xl text-blue-600">/{maxScore}</span>
                      </div>
                      <div className="text-2xl font-semibold text-blue-700 mt-2">
                        {percentage.toFixed(2)}%
                      </div>
                      <p className="text-sm text-blue-700 mt-1 font-medium">
                        Total Score
                      </p>
                    </div>
                    <ScoreProgress value={percentage} showStatus={true} />
                  </div>

                  {/* Core Areas */}
                  {coreTemplates.length > 0 && (
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                        <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                        Core Areas
                      </h4>
                      <div className="space-y-3">
                        {coreTemplates.map((template) => (
                          <div
                            key={template.id}
                            className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200"
                          >
                            <span className="text-sm font-medium text-gray-700">
                              {template.title}
                            </span>
                            <div className="flex items-center space-x-2">
                              <div className="text-right">
                                <span className="text-lg font-bold text-blue-700">
                                  {template.score.toFixed(2)}/{template.maxScore}
                                </span>
                                <div className="text-xs text-gray-600">
                                  ({template.percentage.toFixed(1)}%)
                                </div>
                              </div>
                              {template.score > 0 && (
                                <CheckCircleIcon className="h-5 w-5 text-green-500" />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Supplementary Areas */}
                  {supplementaryTemplates.length > 0 && (
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                        <span className="inline-block w-2 h-2 bg-gray-500 rounded-full mr-2"></span>
                        Supplementary Areas
                      </h4>
                      <div className="space-y-3">
                        {supplementaryTemplates.map((template) => (
                          <div
                            key={template.id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                          >
                            <span className="text-sm font-medium text-gray-700">
                              {template.title}
                            </span>
                            <div className="flex items-center space-x-2">
                              <div className="text-right">
                                <span className="text-lg font-bold text-gray-700">
                                  {template.score.toFixed(2)}/{template.maxScore}
                                </span>
                                <div className="text-xs text-gray-600">
                                  ({template.percentage.toFixed(1)}%)
                                </div>
                              </div>
                              {template.score > 0 && (
                                <CheckCircleIcon className="h-5 w-5 text-green-500" />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Programme Type Badge */}
                  <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg border-2 border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-gray-900">
                        Programme Type
                      </h4>
                      <span className="text-xs font-medium text-blue-700 bg-blue-100 px-3 py-1.5 rounded-full">
                        {programmeTypeDisplay}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-4 flex items-center justify-end space-x-3 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isLoading}
                    className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={onConfirm}
                    disabled={isLoading}
                    className="px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-white">Submitting...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="h-5 w-5 text-white" />
                        <span className="text-white">Confirm Submit</span>
                      </>
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ConfirmationModal;
